import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import { Star } from 'lucide-react';
import type { UserFavoriteWithArche } from '@/lib/api';
import { FavoriteCard } from './FavoriteCard';

interface FavoritesGridProps {
  favorites: UserFavoriteWithArche[];
  onReorder: (archIds: string[]) => void;
  onRemove: (archId: string) => void;
}

export function FavoritesGrid({ favorites, onReorder, onRemove }: FavoritesGridProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = favorites.findIndex((f) => f.archId === active.id);
      const newIndex = favorites.findIndex((f) => f.archId === over.id);
      const reordered = arrayMove(favorites, oldIndex, newIndex);
      onReorder(reordered.map((f) => f.archId));
    }
  };

  if (favorites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Star className="h-12 w-12 text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Your desktop is empty</h3>
        <p className="text-muted-foreground max-w-md">
          Click the star icon on any arche to add it to your desktop for quick access.
        </p>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={favorites.map((f) => f.archId)} strategy={rectSortingStrategy}>
        <div className="grid gap-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {favorites.map((favorite) => (
            <FavoriteCard key={favorite.archId} favorite={favorite} onRemove={onRemove} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
